"use client";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageHero from "@/components/sections/PageHero";
import { motion } from "framer-motion";
import Image from "next/image";
import { HiCheck } from "react-icons/hi";
import { getFacilitiesSpotlight, getAllAmenitiesList } from "@/data/facilities";
import { useLanguage } from "@/lib/i18n";

export default function FacilitiesPage() {
  const { language } = useLanguage();
  const spotlightData = getFacilitiesSpotlight(language);
  const amenitiesData = getAllAmenitiesList(language);

  const pageContent =
    language === "id"
      ? {
          title: "Fasilitas & Layanan Premium",
          subtitle:
            "KinKen menghadirkan ekosistem living yang lengkap untuk gaya hidup urban modern. Setiap fasilitas dirancang dengan standar hotel bintang lima.",
          superText: "Premium Amenities",
          ecosystemTitle: "Ekosistem Living yang Lengkap",
          ecosystemSubtitle:
            "Semua yang Anda butuhkan untuk hidup produktif, sehat, dan seimbang dalam satu lingkungan premium.",
          ctaTitle: "Experience The KinKen Difference",
          ctaSubtitle:
            "Jadwalkan private tour untuk merasakan langsung atmosfer premium KinKen Kost Elite. Tim kami siap menunjukkan setiap detail fasilitas dan menjawab semua pertanyaan Anda.",
          ctaButton: "Jadwalkan Private Tour",
        }
      : {
          title: "Premium Facilities & Services",
          subtitle:
            "KinKen brings a complete living ecosystem for the modern urban lifestyle. Every facility is designed with five-star hotel standards.",
          superText: "Premium Amenities",
          ecosystemTitle: "Complete Living Ecosystem",
          ecosystemSubtitle:
            "Everything you need for a productive, healthy, and balanced life in one premium environment.",
          ctaTitle: "Experience The KinKen Difference",
          ctaSubtitle:
            "Schedule a private tour to experience the premium atmosphere of KinKen Kost Elite firsthand. Our team is ready to show every detail of our facilities and answer all your questions.",
          ctaButton: "Schedule Private Tour",
        };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={pageContent.title}
        subtitle={pageContent.subtitle}
        image="/images/facilities/facilities-hero.webp"
        superText={pageContent.superText}
      />

      {/* SPOTLIGHT SECTION */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 space-y-24 md:space-y-32">
          {spotlightData.map((item, index) => {
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className={`flex flex-col lg:flex-row gap-12 lg:gap-20 items-center ${
                  !isEven ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className="w-full lg:w-1/2 relative h-[300px] md:h-[450px] rounded-sm overflow-hidden shadow-2xl group">
                  <Image
                    src={item.image}
                    alt={item.title}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500" />
                </div>

                <div className="w-full lg:w-1/2 space-y-6">
                  <span className="text-brand-gold text-xs font-bold tracking-[0.2em] uppercase">
                    0{index + 1}
                  </span>
                  <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal">
                    {item.title}
                  </h2>
                  <p className="text-gray-500 font-light text-lg leading-relaxed">
                    {item.desc}
                  </p>

                  <ul className="space-y-3 pt-4">
                    {item.features.map((feat, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-3 text-sm text-gray-600 font-light"
                      >
                        <span className="w-6 h-6 rounded-full bg-[#FAFAFA] border border-gray-100 flex items-center justify-center text-brand-gold">
                          <HiCheck size={14} />
                        </span>
                        {feat}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ALL AMENITIES GRID */}
      <section className="py-20 md:py-32 bg-[#F9F9F9] border-t border-gray-100">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-4">
              {pageContent.ecosystemTitle}
            </h2>
            <p className="text-gray-500 font-light">
              {pageContent.ecosystemSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            {amenitiesData.map((amenity, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05, duration: 0.5 }}
                className="bg-white p-4 md:p-6 lg:p-8 border border-gray-100 hover:border-brand-gold/30 hover:shadow-lg transition-all duration-300 rounded-sm text-center group overflow-hidden"
              >
                <div className="mb-4 md:mb-6 flex justify-center text-gray-400 group-hover:text-brand-gold transition-colors duration-300 transform group-hover:scale-110">
                  <amenity.icon
                    size={24}
                    strokeWidth={1}
                    className="md:w-8 md:h-8"
                  />
                </div>
                <h3 className="font-serif text-sm md:text-base lg:text-lg mb-1 md:mb-2 tracking-wide group-hover:text-brand-gold transition-colors line-clamp-2 break-words">
                  {amenity.title}
                </h3>
                <p className="text-gray-400 text-[10px] md:text-xs leading-relaxed font-light uppercase tracking-wider line-clamp-2">
                  {amenity.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-24 bg-white text-center px-6 border-t border-gray-100">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-serif text-3xl md:text-5xl text-brand-charcoal mb-6">
            {pageContent.ctaTitle}
          </h2>
          <p className="text-gray-500 mb-10 font-light text-lg">
            {pageContent.ctaSubtitle}
          </p>
          <a
            href="https://wa.me/6281916401702"
            target="_blank"
            className="inline-block bg-brand-charcoal text-white px-10 py-5 uppercase text-xs font-bold tracking-[0.2em] hover:bg-brand-gold transition-all duration-300"
          >
            {pageContent.ctaButton}
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}
